import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { setUser } from '../redux/slices/userSlice';
import './Login.css';

const Login = () => {
  const [credentials, setCredentials] = useState({
    email: '',
    password: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Department credentials mapping with specific routes
  const departmentCredentials = {
    'mderp@mail.com': {
      password: 'md123',
      role: 'Managing Director',
      defaultRoute: '/managing-director',
      accessibleModules: ['all'],
      department: 'Management'
    },
    'genralmanagererp@mail.com': {
      password: 'gm123',
      role: 'General Manager',
      defaultRoute: '/gmdashboard',
      accessibleModules: ['General Manager'],
      department: 'General Manager'
    },
    'purchaseerp@mail.com': {
      password: 'purchase123',
      role: 'Purchase Manager',
      defaultRoute: '/purchase',
      accessibleModules: ['purchase'],
      department: 'Purchase & Stores'
    },
    'storeerp@mail.com': {
      password: 'store123',
      role: 'Store Manager',
      defaultRoute: '/store-dashboard',
      accessibleModules: ['store'],
      department: 'Store Management'
    },
    'productionerp@mail.com': {
      password: 'production123',
      role: 'Production Manager',
      defaultRoute: '/pmdashboard',
      accessibleModules: ['ProductionManagement'],
      department: 'Management'
    },
    'saleserp@mail.com': {
      password: 'sales123',
      role: 'Sales Manager',
      defaultRoute: '/sales',
      accessibleModules: ['sales', 'crm'],
      department: 'Sales & CRM'
    },
    'marketingerp@mail.com': {
      password: 'marketing123',
      role: 'Marketing Manager',
      defaultRoute: '/marketing-crm',
      accessibleModules: ['marketing'],
      department: 'Marketing'
    },
    'qualitymgerp@mail.com': {
      password: 'qualitymg123',
      role: 'Quality Manager',
      defaultRoute: '/qualitymanagement',
      accessibleModules: ['QualityManagement'],
      department: 'qualitymanagementmg'
    },
    'amerp@mail.com': {
      password: 'am123',
      role: 'Accounts Manager',
      defaultRoute: '/AccountsManager',
      accessibleModules: ['AccountsManager'],
      department: 'Accounts Management'
    },
    'hrerp@mail.com': {
      password: 'hr123',
      role: 'HR Manager',
      defaultRoute: '/hr-dashboard',
      accessibleModules: ['hr'],
      department: 'Human Resources'
    },
    'employeeerp@mail.com': {
      password: 'employee123',
      role: 'Employee Manager',
      defaultRoute: '/employee-dashboard',
      accessibleModules: ['employee'],
      department: 'Employee Management'
    },
    'supervisorerp@mail.com': {
      password: 'supervisor123',
      role: 'Production Supervisor',
      defaultRoute: '/supervisor-dashboard',
      accessibleModules: ['supervisor'],
      department: 'Supervisor'
    },
    'customererp@mail.com': {
      password: 'customer123',
      role: 'Customer Service Manager',
      defaultRoute: '/customer-dashboard',
      accessibleModules: ['customer'],
      department: 'Customer Service'
    },
    'maintenanceerp@mail.com': {
      password: 'maintenance123',
      role: 'Maintenance Manager',
      defaultRoute: '/maintenance-dashboard',
      accessibleModules: ['maintenance'],
      department: 'Maintenance'
    },
    'securityerp@mail.com': {
      password: 'security123',
      role: 'Security Manager',
      defaultRoute: '/security-dashboard',
      accessibleModules: ['security'],
      department: 'Security'
    },
    'ndperp@mail.com': {
      password: 'ndp123',
      role: 'NDP Manager',
      defaultRoute: '/ndp-dashboard',
      accessibleModules: ['ndp'],
      department: 'New Product Development'
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    const userCreds = departmentCredentials[credentials.email];
    
    if (userCreds && credentials.password === userCreds.password) {
      setTimeout(() => {
        const userData = {
          email: credentials.email,
          role: userCreds.role,
          department: userCreds.department,
          defaultRoute: userCreds.defaultRoute,
          accessibleModules: userCreds.accessibleModules,
          loginTime: new Date().toLocaleString(),
          id: Date.now(),
          name: userCreds.role
        };
        
        // Dispatch to Redux store
        dispatch(setUser(userData));
        
        navigate(userCreds.defaultRoute);
        setIsLoading(false);
      }, 1000);
    } else {
      setTimeout(() => {
        alert('Invalid email or password. Please use the provided demo credentials.');
        setIsLoading(false);
      }, 1000);
    }
  };

  const handleQuickLogin = (email) => {
    const userCreds = departmentCredentials[email];
    if (userCreds) {
      setCredentials({
        email: email,
        password: userCreds.password
      });
      
      setTimeout(() => {
        const userData = {
          email: email,
          role: userCreds.role,
          department: userCreds.department,
          defaultRoute: userCreds.defaultRoute,
          accessibleModules: userCreds.accessibleModules,
          loginTime: new Date().toLocaleString(),
          id: Date.now(),
          name: userCreds.role
        };
        
        // Dispatch to Redux store
        dispatch(setUser(userData));
        
        navigate(userCreds.defaultRoute);
      }, 300);
    }
  };

  const handleEmailChange = (email) => {
    const userCreds = departmentCredentials[email];
    if (userCreds) {
      setCredentials({
        email: email,
        password: userCreds.password
      });
    } else {
      setCredentials({
        ...credentials,
        email: email,
        password: ''
      });
    }
  };

  // All departments for quick access
  const allDepartments = [
    { email: 'mderp@mail.com', name: 'Managing Director', icon: '👑', type: 'Full System Access' },
    { email: 'genralmanagererp@mail.com', name: 'General Manager', icon: '🧑‍💼', type: 'General Manager' },
    { email: 'purchaseerp@mail.com', name: 'Purchase', icon: '🛒', type: 'Purchase' },
    { email: 'storeerp@mail.com', name: 'Store', icon: '📦', type: 'Store' },
    { email: 'productionerp@mail.com', name: 'Production Manager', icon: '🏭', type: 'Production' },
    { email: 'saleserp@mail.com', name: 'Sales', icon: '📢', type: 'Sales' },
    { email: 'marketingerp@mail.com', name: 'Marketing & Research', icon: '📈', type: 'Marketing' },
    { email: 'qualitymgerp@mail.com', name: 'Quality', icon: '✔️', type: 'Quality' },
    { email: 'amerp@mail.com', name: 'Accounts', icon: '📊', type: 'Accounts' },
    { email: 'hrerp@mail.com', name: 'Human Resources (HR)', icon: '👥', type: 'HR Dashboard' },
    { email: 'employeeerp@mail.com', name: 'Employee', icon: '👨‍💼', type: 'Employee' },
    { email: 'supervisorerp@mail.com', name: 'Supervisor', icon: '👔', type: 'Supervisor' },
    { email: 'customererp@mail.com', name: 'Customer', icon: '🤝', type: 'Customer' },
    { email: 'maintenanceerp@mail.com', name: 'Maintenance', icon: '🔧', type: 'Maintenance' },
    { email: 'securityerp@mail.com', name: 'Security Dashboard', icon: '🛡️', type: 'Security Dashboard' },
    { email: 'ndperp@mail.com', name: 'New Product Development', icon: '🔬', type: 'NDP' }
  ];

  return (
    <div className="login-container">
      <div className="login-wrapper">
        {/* Left Side - Department Cards */}
        <div className="departments-section">
          <div className="departments-header">
            <h3>Quick Department Access</h3>
            <p>Click any card to auto-login</p>
          </div>
          <div className="departments-grid">
            {allDepartments.map((dept) => (
              <div 
                key={dept.email}
                className="department-card" 
                onClick={() => handleQuickLogin(dept.email)}
              >
                <div className="dept-icon">{dept.icon}</div>
                <div className="dept-info">
                  <h4>{dept.name}</h4>
                  <p>{dept.email}</p>
                  <div className="dept-password">Click to login</div>
                  <div className="access-type">🔒 {dept.type}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Right Side - Login Form */}
        <div className="login-section">
          <div className="login-card">
            <div className="company-branding">
              <div className="logo-wrapper">
                <img src='/favicon.ico' alt="Logo" className="company-logo" />
              </div>
              <div className="brand-text-wrapper">
                <h1 className="company-name">Ragul Auto Tech</h1>
                <p className="company-description">AI Powered ERP</p>
              </div>
            </div>
            
            <h2 className="login-title">Login</h2>
            
            <form onSubmit={handleSubmit} className="login-form">
              <div className="input-group">
                <div className="input-icon">📧</div>
                <input
                  type="email"
                  value={credentials.email}
                  onChange={(e) => handleEmailChange(e.target.value)}
                  placeholder="Department Email"
                  className="login-input"
                  required
                />
              </div>

              <div className="input-group">
                <div className="input-icon">🔒</div>
                <input
                  type="password"
                  value={credentials.password}
                  onChange={(e) => setCredentials({...credentials, password: e.target.value})}
                  placeholder="Password"
                  className="login-input"
                  required
                />
              </div>

              <button 
                type="submit" 
                className={`login-button ${isLoading ? 'loading' : ''}`}
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <div className="button-spinner"></div>
                    Signing in...
                  </>
                ) : (
                  'Sign In'
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;